import type { FieldValidator, FieldState } from 'final-form';
import _flatMap from 'lodash/flatMap';

/**
 * Compbines an array of validators into a single validator. The validators are run
 * in order until one returns a string, indicating an validator error, or until
 * all validators have been run, returning undefined. ReactFinalForm gives you one
 * validator function, so if we want multiple validators, we compose!
 *
 * @example
 * <Field
 *    validate={composeValidators([REQUIRED, GREATER_THAN_ZERO])}
 *    component={InputFieldV2Adapter}
 *    ...
 * />
 *
 * @returns
 */
export const composeValidators =
  <FieldValue>(...validators: FieldValidator<FieldValue>[]) =>
  (
    value: FieldValue,

    allValues: unknown,
    meta?: FieldState<FieldValue>
  ): unknown | Promise<unknown> => {
    const flatValidators: FieldValidator<FieldValue>[] = _flatMap(validators);
    return flatValidators.reduce((error, validator) => {
      // object is how react-final-form defines it, so we need to use object
      return error || validator(value, allValues as object, meta);
    }, undefined);
  };
